import { inject } from '@angular/core'
import { toObservable } from '@angular/core/rxjs-interop'
import { CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router'

import { Store } from '@ngrx/store'

import { Observable, filter, map, of, take, tap } from 'rxjs'

import { PermissionValue } from '@flowx/designer/modules/workspaces/shared/roles'
import { WorkspacesAndPermissionsStore } from '@flowx/designer/modules/workspaces/workspaces-and-permissions.store'

import { ToastMessageService } from '../services/toast-message.service'
import { selectQueryParams } from '../store/selectors/router.selectors'

export function DefaultPermissionsGuard(permission: PermissionValue): CanActivateFn {
  return (): Observable<boolean | UrlTree> => {
    const router = inject(Router)
    const workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)

    // TODO return a signal once Angular supports it
    return toObservable(workspacesAndPermissionsStore.workspacePermissions.value).pipe(
      filter(Boolean),
      take(1),
      map((permissions) => {
        const isPermitted = permissions[permission]
        return isPermitted ? true : router.parseUrl('/403')
      })
    )
  }
}

export function EditProcessInstanceVariablesGuard(): CanActivateFn {
  return (_, state: RouterStateSnapshot): Observable<boolean | UrlTree> => {
    const store = inject(Store)
    const router = inject(Router)
    const { showEditVariables } = store.selectSignal(selectQueryParams)()
    if (!showEditVariables) {
      return of(true)
    }
    const workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
    const toastMessageService = inject(ToastMessageService)
    return toObservable(workspacesAndPermissionsStore.workspacePermissions.value).pipe(
      filter(Boolean),
      take(1),
      map((permissions) => permissions['wks_process_instance_variables_edit']),
      tap((isPermitted) => {
        if (!isPermitted) {
          toastMessageService.showWarningMessage(`You don't have access to edit process variables.`)
        }
      }),
      map((isPermitted) => {
        if (isPermitted) {
          return true
        }
        const tree = router.parseUrl(state.url)
        delete tree.queryParams['showEditVariables']
        return tree
      })
    )
  }
}
